import { useCallback, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useSelectHasEitherCardTypes, useShowCauseW_Selector } from '@givebacks/api/core';
import type { ConsumerCheckoutCartsResponse } from '@givebacks/api/store';
import { useShowConsumerCheckout } from '@givebacks/api/store';
import { useForm } from '@givebacks/formy';
import { ShopToGiveEnrollmentForm } from 'core/Components';

import { ShopToGiveEnrollmentFormType } from '../checkout.types';

const getCheckoutStoreInfo = (data?: ConsumerCheckoutCartsResponse) => {
  const firstStoreId = data?.checkout.carts[0]?.cause_id;
  return {
    storeId: firstStoreId,
    isMoreThanOneStore: Boolean(data?.checkout.carts.length && data?.checkout.carts.length > 1),
  };
};

const CheckoutShopToGiveEnrollmentSection = ({ checkoutId }: { checkoutId: string }) => {
  const { data: checkoutStoreInfo } = useShowConsumerCheckout({
    checkoutId,
    select: getCheckoutStoreInfo,
  });

  const { data: causeData } = useShowCauseW_Selector({ uuid: checkoutStoreInfo?.storeId });

  const enrollmentDisplayName = useMemo(() => {
    if (checkoutStoreInfo?.isMoreThanOneStore) {
      return 'Your Selected Organizations';
    }

    return causeData?.name ?? '';
  }, [checkoutStoreInfo?.isMoreThanOneStore, causeData?.name]);

  const form = useForm<ShopToGiveEnrollmentFormType>(
    {
      initialValues: {
        terms: false,
      },
      validate: {
        enrollmentOption: (value) => {
          return value == null ? 'Please select an option to continue' : null;
        },
        terms: (value, values) => (!value && !!values.enrollmentOption ? true : null),
      },
    },
    {
      id: 'shop_to_give_enrollment',
    }
  );

  return (
    <ShopToGiveEnrollmentForm
      scrollerRef={form.formRef}
      causeName={enrollmentDisplayName}
      enrollmentOptionFieldProps={{
        ...form.getInputProps('enrollmentOption'),
        onChange: (value) => {
          (form.getInputProps('enrollmentOption').onChange as (value?: boolean) => void)(value);
          form.setFieldValue('terms', false);
        },
      }}
      checkboxFieldProps={form.getInputProps('terms', { type: 'checkbox' })}
      showAgreeToTermsCheckbox={form.values.enrollmentOption}
      enrollmentOptionError={form.errors.enrollmentOption}
      agreeToTermsError={form.errors.terms}
    />
  );
};

const WrappedCheckoutShopToGiveEnrollmentSection = ({ checkoutId }: { checkoutId: string }) => {
  const { data: isPaymentMethodNeeded } = useShowConsumerCheckout({
    checkoutId,
    select: useCallback(
      (data: ConsumerCheckoutCartsResponse) => data.checkout.payment_method_needed,
      []
    ),
    placeholderData: (prev) => prev,
  });
  const { isAuthenticated, isLoading: isAuthenticatedLoading } = useAuthentication();
  const { data: isEnrolled, isLoading: isEnrolledLoading } = useSelectHasEitherCardTypes();

  const isLoading = useMemo(() => {
    return isAuthenticatedLoading || isEnrolledLoading;
  }, [isAuthenticatedLoading, isEnrolledLoading]);

  const showEnrollmentSection = useMemo(() => {
    // Don't show if payment method is not needed
    if (!isPaymentMethodNeeded) return false;

    // Show for unauthenticated users
    if (!isAuthenticated) return true;

    // For authenticated users, only show if they don't have a partner card
    return !isEnrolled;
  }, [isPaymentMethodNeeded, isAuthenticated, isEnrolled]);

  if (!showEnrollmentSection || isLoading) return null;

  return <CheckoutShopToGiveEnrollmentSection checkoutId={checkoutId} />;
};

export { WrappedCheckoutShopToGiveEnrollmentSection as CheckoutShopToGiveEnrollmentSection };
